import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ILocalSearchParameter } from '@fi-shared/models/models';
import { ILightWeightObjectCollection, ILocalSearchIdNameWithPicture } from '@fi-shared/models';
import { FiLocalSearchPropertyType } from '@fi-shared/fi-local-search2/fi-local-search-property-type';
import { BehaviorSubject, interval, Observable, ReplaySubject, Subject } from 'rxjs';
import { isDefined } from '@fi-shared/utils';
import { FiProfileSizes, IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { ControlValueAccessorBase } from '@fi-shared/control-value-accessor-validator-base';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteActivatedEvent } from '@angular/material/autocomplete';
import { FileType, ObjectTypes } from '@fi-shared/types';
import { debounce, takeUntil } from 'rxjs/operators';
import { FloatLabelType, MatFormFieldAppearance } from '@angular/material/form-field';
import { IAddedObject, IAutoCompleteItem, ILocalSearchGroup } from './fi-local-search.d';
import { FiLocalSearch2HelperService } from './fi-local-search2-helper.service';

@Component({
    selector: 'fi-local-search2',
    templateUrl: './fi-local-search2.component.html',
    styleUrls: ['./fi-local-search2.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-local-search2' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiLocalSearch2Component,
        },
    ],
    standalone: false,
})
export class FiLocalSearch2Component
    extends ControlValueAccessorBase<ILocalSearchParameter>
    implements OnInit, OnChanges, OnDestroy
{
    @Input() lightWeightObjectCollection: ILightWeightObjectCollection;
    @Input() searchByContent = true;
    @Input() isSimpleInput = false;
    @Input() isStrictSearch = false;
    @Input() isPrefixIconPresent = true; // todo: remove this input
    @Input() isStandardAutocompleteLayout = false;
    @Input() profilePictureSizeForAutocomplete: FiProfileSizes = FiProfileSizes.Size32px;
    @Input() profilePictureSizeForChips: FiProfileSizes = FiProfileSizes.Size24px;

    @Input() isRegularProfileIconAutocomplete: boolean = false;
    @Input() isRegularProfileIconChips: boolean = false;
    @Input() isRegularStyleProfileIcons: boolean = false;

    @Input() simpleInputAppearance: MatFormFieldAppearance = 'fill';
    @Input() simpleInputClassName = 'simple-search';
    @Input() simpleInputFloatLabelType: FloatLabelType = 'always';
    @Input() simpleInputLabel: string;
    @Input() isPlaceHolderVisible = true;

    @Output() searchTextChange = new EventEmitter<string>();
    @Output() simpleSearchFocus = new EventEmitter<void>();
    @Output() simpleSearchBlur = new EventEmitter<void>();

    private searchInputRef: ElementRef<HTMLInputElement>;
    @ViewChild('searchInput', { read: ElementRef }) set searchInput(elementRef: ElementRef) {
        if (elementRef) this.searchInputRef = elementRef;
    }

    protected searchText: string;
    private searchTextChanged$ = new ReplaySubject<string>(1);

    // private showAllItems: { [prop: number]: boolean } = {}; // ? not used
    private _addedObjects$ = new BehaviorSubject<IAddedObject[]>([]);
    protected get addedObjects$(): Observable<IAddedObject[]> {
        return this._addedObjects$.asObservable();
    }

    private simpleSearchDelayMS = 300;
    private simpleSearchTimer: NodeJS.Timeout;
    private isOptionSelected: boolean = false;

    protected localSearchData$ = new BehaviorSubject<ILocalSearchGroup[]>([]);

    private lastSearchText: string;
    protected readonly FiProfileSizes = FiProfileSizes;

    constructor(
        private cdr: ChangeDetectorRef,
        private fiLocalSearch2HelperService: FiLocalSearch2HelperService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.searchTextChanged$
            .pipe(takeUntil(this.ngOnDestroy$))
            .pipe(debounce(() => interval(this.simpleSearchDelayMS)))
            .subscribe((searchText) => {
                this.searchTextChange.emit(searchText);
            });
    }

    public isEmpty(): boolean {
        return !this.searchText && !this._addedObjects$.value.length;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const change = changes.lightWeightObjectCollection;
        if (change) {
            // this.showAllItems = {}; // ? not used
            this.buildLocalSearchData();
        }
    }

    private buildLocalSearchData(): void {
        let localSearchData: ILocalSearchGroup[] = [];
        if (this.searchText) {
            localSearchData = this.fiLocalSearch2HelperService.getUpdatedLocalSearchGroups(
                this.searchText,
                this.lightWeightObjectCollection,
                this.searchByContent,
            );
        }
        this.localSearchData$.next(localSearchData);
    }

    protected onSearchTextChanged(): void {
        setTimeout(() => (this.lastSearchText = this.searchText));
        this.buildLocalSearchData();
        this.searchTextChanged$.next(this.searchText);
    }

    protected onSimpleSearchTextChanged(): void {
        if (isDefined(this.simpleSearchTimer)) {
            clearTimeout(this.simpleSearchTimer);
        }
        this.simpleSearchTimer = setTimeout(() => {
            this.lastSearchText = this.searchText;
            this.doPropagateChanges(true);
            this.searchTextChanged$.next(this.searchText);
        }, this.simpleSearchDelayMS);
    }

    public doPropagateChanges(isSimpleSearch?: boolean): void {
        if (isSimpleSearch && this.searchText) {
            this.ngModel = this.fiLocalSearch2HelperService.getEmptySearchModel();
            this.ngModel.Names.push(this.searchText);
        } else {
            const objectType: ObjectTypes = this.lightWeightObjectCollection?.ObjectType ?? ObjectTypes.Unknown;
            this.ngModel = this.fiLocalSearch2HelperService.buildSearchModelFromAddedObjects(
                this._addedObjects$.value,
                objectType,
            );
        }

        super.doPropagateChanges();
    }

    private onItemSelected(item: ILocalSearchIdNameWithPicture, propertyType: FiLocalSearchPropertyType): void {
        this.addObject(this.fiLocalSearch2HelperService.getNewAddedObject(item, propertyType));
    }

    private onSeeMatchingSelected(propertyType: FiLocalSearchPropertyType): void {
        this.addObject(
            this.fiLocalSearch2HelperService.getNewAddedObject({ Id: null, Name: this.lastSearchText }, propertyType),
        );
    }

    protected onSearchInputEnterPress($event: Event): void {
        $event.preventDefault();
        if (!this.searchText?.trim().length) {
            return;
        }
        if (!this.isOptionSelected && !this.isStrictSearch) {
            const propertyType = this.searchByContent
                ? FiLocalSearchPropertyType.Content
                : FiLocalSearchPropertyType.Names;
            this.onSeeMatchingSelected(propertyType);
        }
    }

    protected setActivatedOption($event: MatAutocompleteActivatedEvent): void {
        this.isOptionSelected = !!$event.option?.value;
    }

    protected onOptionSelectedChange(optionValue: IAutoCompleteItem): void {
        optionValue.hasOwnProperty('item')
            ? this.onItemSelected(optionValue.item, optionValue.propertyType)
            : this.onSeeMatchingSelected(optionValue.propertyType);
    }

    // todo: refactor this method

    /*
    
    private addObject(
        id: number,
        name: string,
        propertyType: FiLocalSearchPropertyType,
        profilePicture?: string,
        profilePictureExtension?: string,
        fileType?: FileType,
    ) {
    */
    private addObject(objectToAdd: IAddedObject): void {
        const addedObjects = this._addedObjects$.value;
        addedObjects.push(objectToAdd);
        this._addedObjects$.next(addedObjects);
        this.doPropagateChanges();
        this.resetSearchInput();
        setTimeout(() => (this.isOptionSelected = false));
    }

    private resetSearchInput(): void {
        this.searchInputRef.nativeElement.value = '';
        this.searchInputRef.nativeElement.dispatchEvent(
            new Event('input', {
                bubbles: true,
                cancelable: true,
            }),
        );
    }

    protected deleteObject(item: IAddedObject): void {
        const addedObjects = this._addedObjects$.value;
        addedObjects.splice(addedObjects.indexOf(item), 1);
        this._addedObjects$.next(addedObjects);
        this.doPropagateChanges();
        this.cdr.detectChanges();
    }

    protected onDeleteAllAddedObjectsClick(): void {
        this._addedObjects$.next([]);
        this.doPropagateChanges();
    }

    protected getAutocompletePanelClass(): string {
        const classNames: string[] = ['fi-local-search-panel'];
        if (this.isStandardAutocompleteLayout) {
            classNames.push('fi-select-panel-standard');
        }
        return classNames.join(' ');
    }

    protected showSeeMatching(propertyType: FiLocalSearchPropertyType): boolean {
        return this.fiLocalSearch2HelperService.showSeeMatching(this.searchText, propertyType);
    }

    protected getGroupText(group: ILocalSearchGroup): string {
        return this.fiLocalSearch2HelperService.getGroupText(group, this.lightWeightObjectCollection?.ObjectType);
    }

    protected getSeeMatchingText(group: ILocalSearchGroup): string {
        return this.fiLocalSearch2HelperService.getSeeMatchingText(group, this.lightWeightObjectCollection?.ObjectType);
    }

    protected isBusinessUnitIconShown(propertyType: FiLocalSearchPropertyType, physicalFileName: string): boolean {
        return this.fiLocalSearch2HelperService.isBusinessUnitIconShown(
            propertyType,
            physicalFileName,
            this.lightWeightObjectCollection?.ObjectType,
        );
    }

    protected getProfilePictureIcon(propertyType: FiLocalSearchPropertyType, fileType?: FileType): string {
        return this.fiLocalSearch2HelperService.getProfilePictureIcon(
            propertyType,
            this.lightWeightObjectCollection?.ObjectType,
            fileType,
            this.isRegularStyleProfileIcons,
        );
    }

    // protected getAddedObjectColor(propertyType: FiLocalSearchPropertyType): string {
    //     return this.isStandardAutocompleteLayout
    //         ? ''
    //         : this.fiLocalSearch2HelperService.getAddedObjectColor(propertyType);
    // }

    protected isWithProfilePicture(propertyType: FiLocalSearchPropertyType, physicalFileName: string): boolean {
        return this.fiLocalSearch2HelperService.isWithProfilePicture(
            propertyType,
            physicalFileName,
            this.lightWeightObjectCollection?.ObjectType,
        );
    }

    protected getProfileDataFromLocalSearchIdName(
        item: ILocalSearchIdNameWithPicture,
        propertyType: FiLocalSearchPropertyType,
    ): IUserProfile | undefined {
        return this.fiLocalSearch2HelperService.getProfileDataFromLocalSearchIdName(
            item,
            propertyType,
            this.lightWeightObjectCollection?.ObjectType,
        );
    }

    protected getProfileDataFromAddedObject(item: IAddedObject): IUserProfile | undefined {
        return this.fiLocalSearch2HelperService.getProfileDataFromAddedObject(
            item,
            this.lightWeightObjectCollection?.ObjectType,
        );
    }

    protected getSearchInputPlaceholder(): string {
        return this.fiLocalSearch2HelperService.getSearchInputPlaceholder(this.isPlaceHolderVisible);
    }

    protected onSimpleSearchFocus(): void {
        this.simpleSearchFocus.emit();
    }
    protected onSimpleSearchBlur(): void {
        this.simpleSearchBlur.emit();
    }

    public resetSimpleSearchText() {
        this.searchText = '';
        this.onSimpleSearchTextChanged();
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.localSearchData$.complete();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
